import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", message: "", company: "" };

const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = "Please enter your name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Please enter a valid email address.";
  if (f.phone.trim() && !/^[0-9+()\-.\s]{7,40}$/.test(f.phone.trim())) e.phone = "Please enter a valid phone number.";
  if (f.message.trim().length < 10) e.message = "Please write a short message (at least 10 characters).";
  return e;
};

const inputCls = (err) =>
  `mt-2 w-full rounded-xl border bg-white px-4 py-3 text-base text-[#22304A] transition-colors duration-300 placeholder:text-[#6B7280]/70 focus:border-[#6F5B9E] ${err ? "border-[#991B1B]" : "border-[#B9A9D8]/70"}`;

const Field = ({ id, label, error, children }) => (
  <div>
    <label htmlFor={id} className="text-[15px] font-medium text-[#22304A]">{label}</label>
    {children}
    {error && <p id={`${id}-error`} className="mt-1.5 text-sm text-[#991B1B]" data-testid={`contact-${id}-error`}>{error}</p>}
  </div>
);

export const ContactForm = ({ book }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("sending");
    setServerError("");
    try {
      await api.post("/contact", form);
      setStatus("sent");
      setForm(EMPTY);
    } catch (err) {
      setServerError(apiError(err));
      setStatus("idle");
    }
  };

  if (status === "sent") {
    return (
      <div className="card-soft flex flex-col items-start gap-4 p-8" role="status" data-testid="contact-success">
        <CheckCircle2 className="h-8 w-8 text-[#6F5B9E]" aria-hidden="true" />
        <p className="font-display text-2xl text-[#22304A]">{book.success}</p>
        <button onClick={() => setStatus("idle")} className="text-link text-[15px]" data-testid="contact-send-another">Send another message</button>
      </div>
    );
  }

  const aria = (k) => ({ "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `${k}-error` : undefined });
  return (
    <form onSubmit={submit} noValidate method="post" className="card-soft space-y-5 p-6 sm:p-8" data-testid="contact-form">
      <Field id="name" label="Name" error={errors.name}>
        <input id="name" autoComplete="name" value={form.name} onChange={set("name")} className={inputCls(errors.name)} {...aria("name")} data-testid="contact-name-input" />
      </Field>
      <Field id="email" label="Email" error={errors.email}>
        <input id="email" type="email" autoComplete="email" value={form.email} onChange={set("email")} className={inputCls(errors.email)} {...aria("email")} data-testid="contact-email-input" />
      </Field>
      <Field id="phone" label="Phone (optional)" error={errors.phone}>
        <input id="phone" type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} className={inputCls(errors.phone)} {...aria("phone")} data-testid="contact-phone-input" />
      </Field>
      <Field id="message" label="Message" error={errors.message}>
        <textarea id="message" rows={5} value={form.message} onChange={set("message")} className={inputCls(errors.message)} {...aria("message")} data-testid="contact-message-input" />
      </Field>
      <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
        <label htmlFor="company">Company</label>
        <input id="company" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} data-testid="contact-honeypot" />
      </div>
      {serverError && <p role="alert" className="rounded-xl bg-[#991B1B]/5 px-4 py-3 text-sm text-[#991B1B]" data-testid="contact-server-error">{serverError}</p>}
      <div className="flex flex-col gap-4 pt-1">
        <p className="text-sm leading-relaxed text-[#6B7280]" data-testid="contact-privacy-note">{book.privacy_note}</p>
        <button type="submit" disabled={status === "sending"} className="btn-primary w-full disabled:opacity-70 sm:w-auto sm:self-start" data-testid="contact-submit-button">
          {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {status === "sending" ? "Sending…" : "Send message"}
        </button>
      </div>
    </form>
  );
};
